import {ListItem, ListView} from '@github-ui/list-view'
import {SubIssuesActionBar} from './SubIssuesActionBar'
import {useLazyLoadQuery, type FetchPolicy, graphql} from 'react-relay'
import {useCallback, useEffect, useState} from 'react'
import type {SubIssuesListViewQuery} from './__generated__/SubIssuesListViewQuery.graphql'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {SubIssuesSummary} from './SubIssuesSummary'
import {IssuePullRequestTitle} from '@github-ui/list-view-items-issues-prs/IssuePullRequestTitle'
import {IssueItemMetadata} from '@github-ui/list-view-items-issues-prs/IssueItemMetadata'
import {IssuePullRequestStateIcon} from '@github-ui/list-view-items-issues-prs/IssuePullRequestStateIcon'

const SubIssuesListViewGraphQLQuery = graphql`
  query SubIssuesListViewQuery($issueID: ID!) {
    node(id: $issueID) {
      ... on Issue {
        id
        subIssues(first: 50) {
          nodes {
            id
            ...IssuePullRequestTitle
            ...IssuePullRequestStateIcon
            ...IssueItemMetadata
            ...SubIssuesSummary
            title
            titleHTML
            url
            number
            repository {
              name
              owner {
                login
              }
            }
          }
        }
      }
    }
  }
`

export function SubIssuesListView({
  issueID,
  shouldRefetch,
  onRefetch,
  onSubIssueClick,
}: {
  issueID: string
  /** Indicates whether the sub-issues list data should be refetched (e.g., if it is stale) */
  shouldRefetch: boolean
  /** Callback called when we've sent the request to refetch the sub-issues data */
  onRefetch: () => void
  /** Callback called when a sub-issue is clicked */
  onSubIssueClick?: (itemIdentifier: ItemIdentifier, url: string) => void
}) {
  // This looks a little bit unconvential, but this approach was taken from the Relay docs here:
  // https://relay.dev/docs/guided-tour/refetching/refreshing-queries/#when-using-uselazyloadquery
  // In the future, we may be able to replace this refetching with a refetchable fragment that exists
  // in one of the issue-viewer queries, rather than making a separate query and needing to refetch manually.
  const [fetchOptions, setFetchOptions] = useState<{fetchKey: number; fetchPolicy: FetchPolicy} | null>(null)

  const refetch = useCallback(() => {
    setFetchOptions(prevOptions => ({
      fetchKey: (prevOptions?.fetchKey ?? 0) + 1,
      fetchPolicy: 'store-and-network',
    }))
    onRefetch()
  }, [onRefetch])

  const onIssueClick = useCallback(
    (
      event: React.MouseEvent,
      issueNode: {
        number: number
        repository: {
          name: string
          owner: {
            login: string
          }
        }
        url: string
      } & unknown,
    ) => {
      // shortcircuit to default link behaviors if using middle click or modifiers
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.button === 1 || event.shiftKey || event.ctrlKey || event.metaKey) return
      // this is for when a sub-issue is clicked within the sidepanel
      if (onSubIssueClick) {
        event.preventDefault()

        const number = issueNode.number
        const repo = issueNode.repository.name
        const owner = issueNode.repository.owner.login
        const url = issueNode.url

        onSubIssueClick(
          {
            number,
            repo,
            owner,
            type: 'Issue',
          },
          url,
        )
      }
    },
    [onSubIssueClick],
  )

  useEffect(() => {
    // Refetch the sub-issues data when indicated (e.g., data is stale because a sub-issue was added or removed)
    if (shouldRefetch) {
      refetch()
    }
  }, [refetch, shouldRefetch])

  const data = useLazyLoadQuery<SubIssuesListViewQuery>(SubIssuesListViewGraphQLQuery, {issueID}, fetchOptions ?? {})
  if (!data.node?.subIssues) return null

  // TODO: this logic will be added in a follow-up PR
  const getMetadataHref = () => '#'

  return (
    <ListView
      variant="compact"
      title="Sub-issues"
      metadata={
        <ListView.Metadata
          density="condensed"
          title="Sub-issues"
          sx={{background: 'none', height: 40, borderBottom: 0}}
        />
      }
    >
      {data.node.subIssues.nodes?.map(issueNode => {
        if (!issueNode) return null

        const titleValue = issueNode.titleHTML || issueNode.title
        const title = (
          <>
            <IssuePullRequestTitle
              dataKey={issueNode}
              value={titleValue}
              href={issueNode.url}
              target="_blank"
              headerSx={{whiteSpace: 'normal'}}
              onClick={event => onIssueClick(event, issueNode)}
              getLabelHref={getMetadataHref}
            />
            <SubIssuesSummary issue={issueNode} onSummaryClick={event => onIssueClick(event, issueNode)} />
          </>
        )

        const metadata = (
          <IssueItemMetadata
            itemKey={issueNode}
            getMetadataHref={getMetadataHref}
            showAssignees={true}
            showCommentCount={true}
            showCommentZeroCount={false}
          />
        )

        return (
          <ListItem
            key={issueNode.id}
            title={title}
            secondaryActions={<SubIssuesActionBar issueId={issueID} subIssueId={issueNode.id} />}
            metadata={metadata}
            metadataContainerSx={{flexWrap: ['wrap', 'unset']}}
          >
            <ListItem.LeadingContent>
              <IssuePullRequestStateIcon dataKey={issueNode} />
            </ListItem.LeadingContent>
          </ListItem>
        )
      })}
    </ListView>
  )
}

try{ SubIssuesListView.displayName ||= 'SubIssuesListView' } catch {}